import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map, distinctUntilChanged } from 'rxjs';
import { LocationRepository } from '@app/store/location.repository';
import { CategoriesRepository } from '@app/store/categories.repository';
import { BookingRepository } from '@app/store/booking.repository';

@Injectable({
  providedIn: 'root',
})
export class DataPreloadService {
  private initialDataLoadedSubject = new BehaviorSubject<boolean>(false);
  public initialDataLoaded$ = this.initialDataLoadedSubject.asObservable();

  constructor(
    private locationRepo: LocationRepository,
    private categoriesRepo: CategoriesRepository,
    private bookingRepo: BookingRepository,
  ) {
    // Monitor when essential data is loaded
    this.monitorDataLoading();
  }

  private monitorDataLoading(): void {
    // Combine the loading states of essential repositories
    combineLatest([
      this.locationRepo.locations$,
      this.categoriesRepo.categories$,
      this.bookingRepo.bookings$,
    ])
      .pipe(
        map(([locations, categories, bookings]) => {
          // Check if all essential data has been loaded
          const locationsLoaded = locations.isSuccess && locations.data && locations.data.length > 0;
          const categoriesLoaded = categories.isSuccess;
          // Bookings are loaded if the request was successful, regardless of whether there are any bookings
          const bookingsLoaded = bookings.isSuccess;
          
          return locationsLoaded && categoriesLoaded && bookingsLoaded;
        }),
        distinctUntilChanged()
      )
      .subscribe(dataLoaded => {
        this.setInitialDataLoaded(dataLoaded);
      });
  }

  public setInitialDataLoaded(loaded: boolean): void {
    this.initialDataLoadedSubject.next(loaded);
  }

  public resetDataLoadingState(): void {
    this.setInitialDataLoaded(false);
  }

  // Force check the current data loading state
  public checkCurrentDataState(): void {
    combineLatest([
      this.locationRepo.locations$,
      this.categoriesRepo.categories$,
      this.bookingRepo.bookings$,
    ])
      .pipe(
        map(([locations, categories, bookings]) => {
          const locationsLoaded = locations.isSuccess && locations.data && locations.data.length > 0;
          const categoriesLoaded = categories.isSuccess;
          const bookingsLoaded = bookings.isSuccess;
          return locationsLoaded && categoriesLoaded && bookingsLoaded;
        })
      )
      .subscribe(dataLoaded => {
        this.setInitialDataLoaded(dataLoaded);
      });
  }

  // Helper method to check if we have the minimum required data for the app to function
  public get hasRequiredData$(): Observable<boolean> {
    return combineLatest([
      this.locationRepo.locations$,
      this.locationRepo.activeLocation$,
    ]).pipe(
      map(([locations, activeLocation]) => {
        const hasLocations = locations.isSuccess && locations.data && locations.data.length > 0;
        const hasActiveLocation = activeLocation !== undefined;
        return hasLocations && hasActiveLocation;
      })
    );
  }
} 
